import { useEffect, useState } from "react";
import { RefreshCw, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";

export const AdminResults = ({ guard }) => {
  const [doc, setDoc] = useState(null);
  const [votes, setVotes] = useState({});
  const [hours, setHours] = useState(2);
  const [url, setUrl] = useState("");

  const load = (d) => { setDoc(d); setVotes(d.manual_votes || {}); setUrl(d.source_url); setNote(d.thank_you_note || ""); };
  const [note, setNote] = useState("");
  const saveNote = async () => {
    try { const r = await api.put("/admin/results/note", { thank_you_note: note }); load(r.data); toast.success(note.trim() ? "Thank-you note is live." : "Thank-you note hidden."); }
    catch (e) { guard(e); toast.error("Failed to save note."); }
  };
  useEffect(() => {
    let active = true;
    api.get("/admin/results").then((r) => { if (active) load(r.data); }).catch(guard);
    return () => { active = false; };
  }, [guard]);

  const save = async (h) => {
    try {
      const r = await api.put("/admin/results", { manual_votes: votes, override_hours: h, source_url: url });
      load(r.data);
      toast.success(h > 0 ? `Your numbers are live for ${h} hour(s).` : "Back to automatic official results.");
    } catch (e) { guard(e); toast.error("Failed to save."); }
  };
  const refresh = async () => {
    try { const r = await api.post("/admin/results/refresh"); load(r.data); toast.success("Checked official results."); }
    catch (e) { guard(e); toast.error("Refresh failed."); }
  };

  if (!doc) return null;
  return (
    <div data-testid="admin-results" className="max-w-3xl space-y-5">
      <h1 className="font-serif text-2xl font-bold text-navy">Election Results (District 7A)</h1>
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 text-sm" data-testid="results-status">
        <div className="font-semibold text-navy mb-1">
          Now showing: {doc.manual_active ? `Your manual numbers until ${new Date(doc.override_until).toLocaleString()}` : "Automatic official results"}
        </div>
        <div className="text-slate-500">Official feed: {doc.auto_status || "not checked yet"}{doc.auto_updated_at ? ` · last checked ${new Date(doc.auto_updated_at).toLocaleString()}` : ""}</div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          {doc.candidates.map((c) => (
            <div key={c.name} className="text-slate-600">{c.name}: <b>{(doc.auto_votes?.[c.name] ?? 0).toLocaleString()}</b> official</div>
          ))}
        </div>
        <Button onClick={refresh} variant="outline" className="mt-4" data-testid="results-refresh-btn"><RefreshCw className="w-4 h-4 mr-2" /> Check official results now</Button>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
        <h2 className="font-serif font-bold text-navy">Type vote counts (overrides the automatic feed)</h2>
        {doc.candidates.map((c) => (
          <label key={c.name} className="flex items-center justify-between gap-4">
            <span className="text-sm font-semibold text-slate-700">{c.name} <span className="text-slate-400 font-normal">({c.party})</span></span>
            <Input type="number" min="0" className="w-40" value={votes[c.name] ?? 0}
              onChange={(e) => setVotes({ ...votes, [c.name]: Number(e.target.value) })}
              data-testid={`results-input-${c.name.split(" ").pop().toLowerCase()}`} />
          </label>
        ))}
        <label className="flex items-center justify-between gap-4">
          <span className="text-sm font-semibold text-slate-700">Show my numbers for (hours)</span>
          <Input type="number" min="0.25" step="0.25" className="w-40" value={hours} onChange={(e) => setHours(Number(e.target.value))} data-testid="results-override-hours" />
        </label>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => save(hours)} className="bg-brightblue hover:bg-brightblue-hover text-white" data-testid="results-save-btn"><Save className="w-4 h-4 mr-2" /> Save & show my numbers</Button>
          <Button onClick={() => save(0)} variant="outline" data-testid="results-auto-btn">Switch back to automatic</Button>
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
        <label className="block"><span className="text-sm font-semibold text-slate-700 mb-1.5 block">Thank-you note from Satish <span className="text-slate-400 font-normal">(shows under the results; leave empty to hide)</span></span>
          <Textarea rows={4} value={note} onChange={(e) => setNote(e.target.value)} data-testid="results-note-input" /></label>
        <Button onClick={saveNote} className="mt-3 bg-brightblue hover:bg-brightblue-hover text-white" data-testid="results-note-save-btn"><Save className="w-4 h-4 mr-2" /> Save note</Button>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
        <label className="block"><span className="text-sm font-semibold text-slate-700 mb-1.5 block">Official results page address (Maryland State Board of Elections)</span>
          <Input value={url} onChange={(e) => setUrl(e.target.value)} data-testid="results-source-url" /></label>
        <p className="text-xs text-slate-400 mt-2">Saved with either button above. Change this if the state posts results at a different address on election night.</p>
      </div>
    </div>
  );
};
